import UserPlusIcon from "@/assets/colorable/user_plus.svg?react";
import BetaChip from "@/components/Common/BetaChip";
import LanguageSelector from "@/components/Common/LanguageSelector";
import Notifications from "@/components/NavBar/Notifications";
import {
  useIsAgency,
  useIsCompany,
  useIsInfuencer,
  useIsSubbrand,
  useSelectedCompanyReactive,
} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {
  brandImpersonationAtom,
  isAgencyInviteModalOpenAtom,
  isMobileSidebarOpenAtom,
  multiAccountInviteModalAtom,
} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {useLocation} from "@tanstack/react-router";
import dayjs from "dayjs";
import {useAtomValue, useSetAtom} from "jotai";
import {Menu} from "lucide-react";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const BETA_ROUTES = ["/search-visibility"];

const TopBar: FC = () => {
  const {t} = useTranslation(["navigation", "common", "search_visibility"]);
  const location = useLocation();
  const selectedCompany = useSelectedCompanyReactive();
  const isAgency = useIsAgency();
  const isCompany = useIsCompany();
  const isSubbrand = useIsSubbrand();
  const isInfluencer = useIsInfuencer();
  const isInfluencerUser = isInfluencer && !isCompany && !isSubbrand && !isAgency;
  const setInviteModal = useSetAtom(multiAccountInviteModalAtom);
  const setAgencyInviteModal = useSetAtom(isAgencyInviteModalOpenAtom);
  const setMobileSidebarOpen = useSetAtom(isMobileSidebarOpenAtom);
  const user = useUserStore(state => state.user);
  const isImpersonatingBrand = !!useAtomValue(brandImpersonationAtom);

  const onInviteButtonClick = () => {
    if (!selectedCompany) {
      setAgencyInviteModal(true);
    } else {
      setInviteModal({
        isVisible: true,
        isViewingOwnCompany: true,
      });
    }
  };

  const getPageTitle = () => {
    if (location.pathname.includes("/dashboard")) return t("campaigns");
    if (location.pathname.includes("/admins/customers")) return t("customers");
    if (location.pathname.includes("/admins/customer-success")) return t("customer_success");
    if (location.pathname.includes("/search-visibility")) return t("search_visibility:tiktok_search_visibility");
    if (location.pathname.includes("/media-library")) return t("media_library");
    if (location.pathname.includes("/groups")) return t("groups");
    if (location.pathname.includes("/influencers")) return t("influencers");
    if (location.pathname.includes("/chat")) return t("messages");
    if (location.pathname.includes("/plans")) return t("common:ourPlans");
    if (location.pathname.includes("/invoices")) return t("payment_history");
    if (location.pathname === "/campaigns") return isInfluencerUser ? t("campaigns") : t("campaign_feed");
    return null;
  };

  const pageTitle = getPageTitle();

  return (
    <header
      className={clsxm(
        "sticky top-0 z-999 flex h-16 shrink-0 items-center justify-between bg-white/80 px-6 backdrop-blur-md",
        isImpersonatingBrand && "top-14",
      )}>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => setMobileSidebarOpen(true)}
          aria-label="Open menu"
          className="text-muted-foreground hover:text-foreground md:hidden">
          <Menu className="size-5" />
        </button>
        {pageTitle && (
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h1 className="text-foreground m-0 text-lg leading-tight font-bold">{pageTitle as string}</h1>
              {BETA_ROUTES.some(route => location.pathname.includes(route)) && <BetaChip />}
            </div>
            <span className="text-muted-foreground text-xs uppercase">{dayjs().format("MMM D, YYYY")}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-3">
        {user.has_multi_account_users_access && (
          <Can I="create" a="CompanyMemberInvitation">
            <button
              onClick={onInviteButtonClick}
              className="border-border text-foreground hover:bg-muted hidden h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors sm:flex">
              <UserPlusIcon className="size-4 shrink-0" />
              {t("invite")}
            </button>
          </Can>
        )}
        {!!selectedCompany && <Notifications className="flex flex-col" />}
        <LanguageSelector />
      </div>
    </header>
  );
};

export default TopBar;
